<template>
  <div class="container" @click="$emit('titleClicked')">
    <div class="document-card-wrapper">
      <div class="image-container">
        <div v-if="!loaded" class="placeholder">
          <slot name="placeholder">
            <div class="skeleton" />
          </slot>
        </div>
        <img v-show="loaded" :src="thumbUrl" alt="thumb" @load="onLoad" @error="onError" />
      </div>
      <div class="document-details">
        <h4 class="document-title" @click.stop="$emit('titleClicked')">
          {{ title }}
        </h4>
      </div>
    </div>
    <div class="snippet-container">
      <div v-if="!display2ndOrderReferences">
        <div>
          <span>{{ 'Excerpt from Page #' + page }}</span>
        </div>
        <div class="snippet">
          {{ snippet }}
        </div>
      </div>
      <div v-else>
        <ReferenceDetailsLabels :source="source" :doc-type="reference.doc_type" />

        <div class="reference-details-wrapper" @click.stop>
          <ReferenceDetails
            :reference="reference"
            @preview="$emit('preview', $event)"
            placeholder="conversation.referenceCard.originalText"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';

import ReferenceDetails from '~/components/conversation/referencePanel/ReferenceDetails.vue';
import ReferenceDetailsLabels from '~/components/conversation/referencePanel/ReferenceDetailsLabels.vue';
import useConversations from '~/composables/useConversations';

export default {
  components: { ReferenceDetails, ReferenceDetailsLabels },
  emits: ['titleClicked', 'preview'],
  props: {
    title: {
      type: String,
      default: '',
    },
    page: {
      type: String,
      default: '',
    },
    snippet: {
      type: String,
      default: '',
    },
    thumbUrl: {
      type: String,
      default: '',
    },
    source: {
      type: Object,
      default: () => ({}),
    },
    reference: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    const { display2ndOrderReferences } = useConversations();

    const loaded = ref(false);

    const onLoad = () => (loaded.value = true);
    const onError = () => (loaded.value = false);

    return {
      loaded,
      onError,
      onLoad,
      display2ndOrderReferences,
    };
  },
};
</script>

<style scoped lang="scss">
.container {
  background-color: $bb-neutral-0;
  width: 300px;
  max-height: 232px;
  overflow: hidden;
  border: 1px solid #ddd;
  border-radius: $bb-spacing-2;
  box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column; /* Stack rows */
  z-index: 1000;

  .snippet-container {
    display: flex;
    flex-direction: column;
    gap: $bb-spacing-2;
    padding-inline: $bb-spacing-3;
    max-height: 260px; /* Subtract padding */

    .reference-details-wrapper {
      margin-bottom: $bb-spacing-2;
      font-size: 10px;
      @include bb-regular-text-styles('xs');
      overflow: hidden;
      color: $bb-neutral-1;
      display: -webkit-box;
      line-clamp: 2;
      -webkit-line-clamp: 2; /* Number of lines before ellipsis */
      -webkit-box-orient: vertical;
      box-sizing: border-box;
      padding-top: 0;
      padding-bottom: 0; /* Prevents adding extra height */
      margin-bottom: $bb-spacing-3;
    }

    .snippet {
      @include bb-regular-text-styles('xs');
      overflow: hidden;
      color: $bb-neutral-1;
      display: -webkit-box;
      line-clamp: 5;
      -webkit-line-clamp: 5; /* Number of lines before ellipsis */
      -webkit-box-orient: vertical;
      box-sizing: border-box;
      padding-top: 0;
      padding-bottom: 0; /* Prevents adding extra height */
      margin-bottom: $bb-spacing-3;
    }

    span {
      @include bb-medium-text-styles('xs');
      color: $bb-neutral-1;
      text-decoration: underline;
      letter-spacing: 0.1px;
      font-size: 13px;
    }
  }

  .document-card-wrapper {
    display: flex;
    flex-direction: row;
    gap: $bb-spacing-2;
    margin-bottom: $bb-spacing-3;
    background-color: $bb-neutral-6;
    padding: $bb-spacing-4;

    .image-container {
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: $bb-spacing-1;
      background-color: $bb-neutral-5;
      width: 64px;
      min-width: 64px;
      height: 64px;
      gap: $bb-spacing-1;
      position: relative;

      .placeholder {
        position: absolute;
        inset: 0;
      }

      .skeleton {
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, #eee 25%, #f5f5f5 37%, #eee 63%);
        animation: shimmer 1.4s infinite;
      }

      @keyframes shimmer {
        0% {
          background-position: -200px 0;
        }
        100% {
          background-position: 200px 0;
        }
      }

      .default-thumb {
        align-self: flex-start;
        width: 60px;
        height: 48px;
        background-color: $bb-neutral-4;
      }

      img {
        max-width: 60px;
        max-height: 48px;
        align-self: center;
      }
    }

    .document-details {
      flex-grow: 1;
      display: flex;
      flex-direction: column;
      gap: $bb-spacing-2;
      max-width: 100%;
      overflow: hidden;

      .document-title {
        @include bb-medium-text-styles('md');
        line-height: $bb-spacing-5;
        color: $bb-neutral-1;
        cursor: pointer;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        line-clamp: 2;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
      }
    }
  }
}
</style>
